/**
 * PaginationManager - Handles pagination logic and UI for product grids
 * Can be used by any grid controller
 */

class PaginationManager {
  constructor(gridManager) {
    this.gridManager = gridManager;
    this.debounceTimer = null;
    this.DEBOUNCE_DELAY = 300; // 300ms delay to prevent rapid clicking

    this.setupPaginationElements();
  }

  setupPaginationElements() {
    this.paginationTop = document.getElementById('pagination-top-content');
    this.paginationBottom = document.getElementById('pagination-bottom-content');
    this.paginationTopLoading = document.getElementById('pagination-top-loading');
    this.paginationBottomLoading = document.getElementById('pagination-bottom-loading');
    this.infoTop = document.getElementById('pagination-info-top');
    this.infoBottom = document.getElementById('pagination-info-bottom');
    this.pageNumbersTop = document.getElementById('page-numbers-top');
    this.pageNumbersBottom = document.getElementById('page-numbers-bottom');

    this.firstButtons = document.querySelectorAll('#first-page-top, #first-page-bottom');
    this.prevButtons = document.querySelectorAll('#prev-page-top, #prev-page-bottom');
    this.nextButtons = document.querySelectorAll('#next-page-top, #next-page-bottom');
    this.lastButtons = document.querySelectorAll('#last-page-top, #last-page-bottom');
  }

  render(totalFiltered, totalPages, options = {}) {
    const { simpleMode = false } = options;
    
    if (totalPages <= 1 && !simpleMode) {
      this.hide();
      return;
    }

    this.show();
    
    if (simpleMode) {
      // Simple mode: Only show Prev/Next, hide page numbers and first/last
      this.renderSimpleMode();
    } else {
      // Full mode: Show all pagination controls
      this.updateInfo(totalFiltered);
      this.updateButtons(totalPages);
      this.updatePageNumbers(totalPages);
      this.showFullControls();
    }
  }

  renderSimpleMode() {
    // Hide first/last buttons and page numbers
    this.firstButtons.forEach(btn => btn.style.display = 'none');
    this.lastButtons.forEach(btn => btn.style.display = 'none');
    if (this.pageNumbersTop) this.pageNumbersTop.style.display = 'none';
    if (this.pageNumbersBottom) this.pageNumbersBottom.style.display = 'none';
    if (this.infoTop) this.infoTop.parentElement.style.display = 'none';
    if (this.infoBottom) this.infoBottom.parentElement.style.display = 'none';
    
    // Enable prev/next based on current page (prev disabled on page 1, next always enabled in simple mode)
    this.prevButtons.forEach(btn => {
      btn.style.display = '';
      btn.disabled = this.gridManager.currentPage <= 1;
      btn.onclick = () => {
        if (this.gridManager.currentPage > 1 && !this.debounceTimer) {
          this.gridManager.currentPage--;
          if (this.gridManager.awaitingPaginationCount && this.gridManager.loadWithFilters) {
            this.gridManager.updateURL();
            this.gridManager.showPaginationLoading();
            this.gridManager.loadWithFilters();
          } else {
            this.debouncedRender();
            this.provideFeedback(btn, () => btn.disabled = this.gridManager.currentPage <= 1);
          }
        }
      };
    });

    this.nextButtons.forEach(btn => {
      btn.style.display = '';
      btn.disabled = false; // Always enabled in simple mode (we know there's more)
      btn.onclick = () => {
        if (!this.debounceTimer) {
          this.gridManager.currentPage++;
          if (this.gridManager.awaitingPaginationCount && this.gridManager.loadWithFilters) {
            this.gridManager.updateURL();
            this.gridManager.showPaginationLoading();
            this.gridManager.loadWithFilters();
          } else {
            this.debouncedRender();
          }
        }
      };
    });
  }

  showFullControls() {
    // Show all controls (used when switching from simple to full mode)
    this.firstButtons.forEach(btn => btn.style.display = '');
    this.lastButtons.forEach(btn => btn.style.display = '');
    if (this.pageNumbersTop) this.pageNumbersTop.style.display = '';
    if (this.pageNumbersBottom) this.pageNumbersBottom.style.display = '';
    if (this.infoTop) this.infoTop.parentElement.style.display = '';
    if (this.infoBottom) this.infoBottom.parentElement.style.display = '';
  }

  showLoading() {
    // Show loading spinners, hide pagination content
    if (this.paginationTopLoading) this.paginationTopLoading.style.display = 'flex';
    if (this.paginationBottomLoading) this.paginationBottomLoading.style.display = 'flex';
    if (this.paginationTop) this.paginationTop.style.display = 'none';
    if (this.paginationBottom) this.paginationBottom.style.display = 'none';
  }

  show() {
    // Hide loading spinners
    if (this.paginationTopLoading) this.paginationTopLoading.style.display = 'none';
    if (this.paginationBottomLoading) this.paginationBottomLoading.style.display = 'none';
    // Show pagination content
    if (this.paginationTop) this.paginationTop.style.display = 'flex';
    if (this.paginationBottom) this.paginationBottom.style.display = 'flex';
  }

  hide() {
    // Hide both loading spinners and pagination content
    if (this.paginationTopLoading) this.paginationTopLoading.style.display = 'none';
    if (this.paginationBottomLoading) this.paginationBottomLoading.style.display = 'none';
    if (this.paginationTop) this.paginationTop.style.display = 'none';
    if (this.paginationBottom) this.paginationBottom.style.display = 'none';
  }

  updateInfo(totalFiltered) {
    const startIndex = (this.gridManager.currentPage - 1) * this.gridManager.productsPerPage + 1;
    const endIndex = Math.min(this.gridManager.currentPage * this.gridManager.productsPerPage, totalFiltered);
    const infoText = `Showing ${startIndex}-${endIndex} of ${totalFiltered} items`;

    if (this.infoTop) this.infoTop.textContent = infoText;
    if (this.infoBottom) this.infoBottom.textContent = infoText;
  }

  updateButtons(totalPages) {
    this.totalPages = totalPages;

    this.firstButtons.forEach(btn => {
      btn.disabled = this.gridManager.currentPage <= 1;
      btn.onclick = () => {
        if (this.gridManager.currentPage > 1 && !this.debounceTimer) {
          this.gridManager.currentPage = 1;
          this.debouncedRender();
          this.provideFeedback(btn, () => btn.disabled = this.gridManager.currentPage <= 1);
        }
      };
    });

    this.prevButtons.forEach(btn => {
      btn.disabled = this.gridManager.currentPage <= 1;
      btn.onclick = () => {
        if (this.gridManager.currentPage > 1 && !this.debounceTimer) {
          this.gridManager.currentPage--;
          this.debouncedRender();
          this.provideFeedback(btn, () => btn.disabled = this.gridManager.currentPage <= 1);
        }
      };
    });

    this.nextButtons.forEach(btn => {
      btn.disabled = this.gridManager.currentPage >= totalPages;
      btn.onclick = () => {
        if (this.gridManager.currentPage < totalPages && !this.debounceTimer) {
          this.gridManager.currentPage++;
          this.debouncedRender();
          this.provideFeedback(btn, () => btn.disabled = this.gridManager.currentPage >= totalPages);
        }
      };
    });

    this.lastButtons.forEach(btn => {
      btn.disabled = this.gridManager.currentPage >= totalPages;
      btn.onclick = () => {
        if (this.gridManager.currentPage < totalPages && !this.debounceTimer) {
          this.gridManager.currentPage = totalPages;
          this.debouncedRender();
          this.provideFeedback(btn, () => btn.disabled = this.gridManager.currentPage >= this.totalPages);
        }
      };
    });
  }

  updatePageNumbers(totalPages) {
    let pageHTML = '';
    const maxVisible = 5;
    let startPage = Math.max(1, this.gridManager.currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      const activeClass = i === this.gridManager.currentPage ? 'active' : '';
      pageHTML += `<button class="page-number ${activeClass}" onclick="window.PaginationManager.goToPage(${i})">${i}</button>`;
    }

    if (this.pageNumbersTop) this.pageNumbersTop.innerHTML = pageHTML;
    if (this.pageNumbersBottom) this.pageNumbersBottom.innerHTML = pageHTML;
  }

  goToPage(page) {
    if (!this.debounceTimer && this.gridManager.currentPage !== page) {
      console.log(`Page changed from ${this.gridManager.currentPage} to ${page}`);

      this.gridManager.currentPage = page;
      this.debouncedRender();

      // Provide immediate visual feedback
      const pageButtons = document.querySelectorAll('.page-number');
      pageButtons.forEach(btn => {
        this.provideFeedback(btn, () => btn.disabled = false);
      });
    }
  }

  debouncedRender() {
    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Update URL and grid state
    this.gridManager.updateURL();

    // Set new timer
    this.debounceTimer = setTimeout(() => {
      this.gridManager.renderGrid();
      this.debounceTimer = null;
    }, this.DEBOUNCE_DELAY);
  }

  provideFeedback(button, restoreFunction) {
    button.disabled = true;
    setTimeout(() => {
      restoreFunction();
    }, this.DEBOUNCE_DELAY);
  }

  // Static method for global access (used by onclick handlers)
  static goToPage(page) {
    if (window.activePaginationManager) {
      window.activePaginationManager.goToPage(page);
    }
  }
}

// Export for use
window.PaginationManager = PaginationManager;
